/* istanbul ignore file reason: TODO */

import { Injectable, InjectionToken, Inject } from '@angular/core';
import { FirebaseConfig } from '@ionic-native/firebase-config/ngx';
import { Deploy } from 'cordova-plugin-ionic/dist/ngx';
import { Market } from '@ionic-native/market/ngx';
import { AlertController, LoadingController, ModalController, NavController, Platform, ToastController } from '@ionic/angular';
import { Router, NavigationEnd } from '@angular/router';
import { filter, take } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { LiveUpdatePage } from '../modals/live-update/live-update.page';

export interface UpdateAppConfig {
  key: string;
  appName: string;
  appVersion: string;
  deployChannel: string;
  packageId: string;
  appStoreId: string;
  intervalDuration?: number;
  unsafeUrls?: string[];
}

export const UpdateAppConfig = new InjectionToken<UpdateAppConfig>('UpdateAppConfig');

export enum UpdateType {
  None,
  Notify,
  Live,
  Force,
}

interface VersionsPayload {
  latest: string;
  versions: { [key: string]: UpdateType };
}

const DefaultInterval = 60_000 * 5; // 5 minutes

@Injectable()
export class UpdateAppService {
  private oldUpdateType: UpdateType = UpdateType.None;
  private latestVersion: string;

  constructor(
    @Inject(UpdateAppConfig) private config: UpdateAppConfig,
    private firebaseConfig: FirebaseConfig,
    private router: Router,
    private alertCtrl: AlertController,
    private toastCtrl: ToastController,
    private loadingCtrl: LoadingController,
    private deploy: Deploy,
    private platform: Platform,
    private market: Market,
    private translate: TranslateService,
    private navController: NavController,
    private modalController: ModalController
  ) {}

  async initialize() {
    await this.fetchAndUpdate();
  }

  private async fetchAndUpdate() {
    await this.firebaseConfig.fetch(0);
    await this.firebaseConfig.activate();

    const updateType = await this.getUpdateType();
    if (this.canUpdate(updateType)) {
      this.oldUpdateType = updateType;

      if (this.isSafeToUpdate()) {
        await this.doUpdate(updateType);
      } else {
        this.router.events
          .pipe(
            filter(event => event instanceof NavigationEnd && this.isSafeToUpdate()),
            take(1)
          )
          .subscribe(() => this.doUpdate(updateType));
      }
    }
  }

  private async getUpdateType() {
    const payload = await this.getPayload();
    if (!payload || payload.latest === this.config.appVersion) return UpdateType.None;
    this.latestVersion = payload.latest;

    if (getMajor(payload.latest) !== getMajor(this.config.appVersion)) return UpdateType.Force;

    const appVersionInt = versionToInt(this.config.appVersion);
    const latestVersionInt = versionToInt(payload.latest);
    const sortedUpdates = Object.entries(payload.versions)
      .map(([version, type]) => ({
        type,
        version,
        versionInt: versionToInt(version),
      }))
      .filter(update => update.versionInt > appVersionInt && update.versionInt <= latestVersionInt)
      .sort((a, b) => a.versionInt - b.versionInt);

    let updateType = UpdateType.None;
    for (const update of sortedUpdates) {
      updateType = Math.max(update.type, updateType);
      if (updateType === UpdateType.Force) break;
    }

    return updateType;
  }

  private async getPayload() {
    const value = await this.firebaseConfig.getString(this.config.key);
    if (!value) return null;
    try {
      return JSON.parse(value) as VersionsPayload;
    } catch {
      return null;
    }
  }

  private canUpdate(type: UpdateType) {
    // do not show normal update again
    if (type === this.oldUpdateType && type === UpdateType.Notify) return false;
    return true;
  }

  private isSafeToUpdate() {
    return this.config.unsafeUrls?.some(url => this.router.isActive(url, false)) !== true;
  }

  private async doUpdate(type: UpdateType) {
    const openStoreHandler = () => {
      if (this.platform.is('android')) {
        this.market.open(this.config.packageId);
        return false;
      } else if (this.platform.is('ios')) {
        this.market.open(this.config.appStoreId);
        return false;
      }
    };

    const translateParams = {
      appName: this.config.appName,
      storeName: this.platform.is('android') ? 'play store' : 'app store',
      version: `(v${this.latestVersion})`,
    };

    switch (type) {
      case UpdateType.Force: {
        const alert = await this.alertCtrl.create({
          header: this.translate.instant('AppUpdate.forceUpdateTitle'),
          message: this.translate.instant('AppUpdate.forceUpdateDescription', {
            ...translateParams,
            storeName: translateParams.storeName
              .split(' ')
              .map(w => w.charAt(0).toUpperCase() + w.slice(1))
              .join(' '), // capitalize first letter
          }),
          buttons: [{ text: this.translate.instant('AppUpdate.forceUpdateButton', translateParams), handler: openStoreHandler }],
          backdropDismiss: false,
          keyboardClose: true,
        });
        await alert.present();
        break;
      }
      case UpdateType.Notify: {
        const alert = await this.alertCtrl.create({
          header: this.translate.instant('AppUpdate.notifyUpdateTitle'),
          message: this.translate.instant('AppUpdate.notifyUpdateDescription', translateParams),
          buttons: [
            { text: this.translate.instant('AppUpdate.notifyUpdateButtonA'), role: 'cancel' },
            {
              text: this.translate.instant('AppUpdate.notifyUpdateButtonB'),
              handler: openStoreHandler,
            },
          ],
          backdropDismiss: false,
          keyboardClose: true,
        });
        await alert.present();
        break;
      }
      case UpdateType.Live: {
        const modal = await this.modalController.create({
          component: LiveUpdatePage,
          cssClass: 'live-update-modal',
          backdropDismiss: false,
          componentProps: {
            version: this.latestVersion,
          },
        });

        modal.present();
      }
    }

    this.scheduleNextUpdate(type);
  }

  private scheduleNextUpdate(type: UpdateType) {
    switch (type) {
      case UpdateType.Notify:
      case UpdateType.None:
        setTimeout(() => this.fetchAndUpdate(), this.config.intervalDuration ?? DefaultInterval);
        break;
    }
  }
}

function versionToInt(version: string) {
  return version.split('.').reduceRight((num, digit, index, digits) => {
    const len = digits.length;
    const invertIndex = Math.abs(index - (len - 1)) % len;
    const padding = invertIndex * 2;

    return num + Number(digit) * 10 ** padding;
  }, 0);
}

function getMajor(version: string) {
  return Number(version.split('.').shift());
}
